import { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import confetti from "canvas-confetti";
import { Flower2, Leaf, Snowflake, Sun, Volume2, VolumeX } from "lucide-react";
import { api } from "@/lib/api";
import { currentSeasonIndex, SEASONS } from "./SeasonalHero";

export const SEASON_ICONS: Record<string, any> = { flower: Flower2, sun: Sun, leaf: Leaf, snowflake: Snowflake };
const COUNT = 5;
let actx: AudioContext | null = null;

function chime() {
  try {
    actx ||= new (window.AudioContext || (window as any).webkitAudioContext)();
    const t = actx.currentTime;
    [880, 1318.5].forEach((f, i) => {
      const o = actx!.createOscillator(), g = actx!.createGain();
      o.type = "sine"; o.frequency.value = f;
      g.gain.setValueAtTime(0, t + i * 0.07); g.gain.linearRampToValueAtTime(0.12, t + i * 0.07 + 0.02); g.gain.exponentialRampToValueAtTime(0.001, t + i * 0.07 + 0.6);
      o.connect(g).connect(actx!.destination); o.start(t + i * 0.07); o.stop(t + i * 0.07 + 0.65);
    });
  } catch { /* audio unavailable */ }
}

const spawn = (i: number) => ({ id: `${i}-${Math.random()}`, left: 6 + Math.random() * 84, dur: 14 + Math.random() * 8, delay: -(Math.random() * 12), sway: (Math.random() - 0.5) * 120, size: 2.3 + Math.random() * 0.8 });

function Sparkle({ x, y }: { x: number; y: number }) {
  return (
    <div className="pointer-events-none fixed z-[60]" style={{ left: x, top: y }} data-testid="catch-sparkle">
      {Array.from({ length: 8 }, (_, i) => {
        const a = (i / 8) * Math.PI * 2;
        return (
          <motion.span key={i} className="absolute h-2.5 w-2.5 rounded-full bg-amber-300 shadow-[0_0_8px_2px_rgba(253,224,71,.8)]"
            initial={{ x: 0, y: 0, scale: 1, opacity: 1 }} animate={{ x: Math.cos(a) * 46, y: Math.sin(a) * 46, scale: 0.2, opacity: 0 }} transition={{ duration: 0.6, ease: "easeOut" }} />
        );
      })}
      <motion.span className="absolute -left-5 -top-5 text-3xl font-bold text-amber-500" initial={{ y: 0, opacity: 1 }} animate={{ y: -40, opacity: 0 }} transition={{ duration: 0.8 }}>+1</motion.span>
    </div>
  );
}

export function KidsCatch({ kidId }: { kidId: string }) {
  const season = SEASONS[currentSeasonIndex()];
  const [state, setState] = useState<any>(null);
  const [pieces, setPieces] = useState(() => Array.from({ length: COUNT }, (_, i) => spawn(i)));
  const [sparks, setSparks] = useState<{ id: number; x: number; y: number }[]>([]);
  const [won, setWon] = useState<any>(null);
  const [sound, setSound] = useState(() => localStorage.getItem("cast_catch_sound") !== "off");
  const busy = useRef(0);
  useEffect(() => { api.get(`/kids/${kidId}/catches`).then((r) => setState(r.data)).catch(() => {}); }, [kidId]);

  const caught = async (idx: number, e: React.PointerEvent) => {
    const sp = { id: Date.now() + idx, x: e.clientX, y: e.clientY };
    setSparks((s) => [...s, sp]);
    setTimeout(() => setSparks((s) => s.filter((x) => x.id !== sp.id)), 900);
    setPieces((ps) => ps.map((p, i) => (i === idx ? { ...spawn(i), delay: 0.6 } : p)));
    if (sound) chime();
    setState((s: any) => s && { ...s, today: s.today + 1 });
    busy.current++;
    try {
      const { data } = await api.post(`/kids/${kidId}/catch`);
      setState(data);
      window.dispatchEvent(new Event("cast-caught"));
      if (data.new_sticker) {
        setWon(data.new_sticker);
        confetti({ particleCount: 140, spread: 100, origin: { y: 0.4 }, colors: [data.new_sticker.color, "#FDE68A", "#FFFFFF"], disableForReducedMotion: true });
      }
    } catch { /* keep playing offline */ } finally { busy.current--; }
  };
  const toggleSound = () => { const v = !sound; setSound(v); localStorage.setItem("cast_catch_sound", v ? "on" : "off"); };
  const Icon = SEASON_ICONS[state?.season?.icon] || Leaf;
  const progress = state ? Math.min(state.today, state.goal) : 0;
  const mote = season.particle === "mote";

  return (
    <>
      <div className="pointer-events-none fixed inset-0 z-30 overflow-hidden" data-testid="kids-catch-layer" data-particle={season.particle}>
        {pieces.map((p, i) => (
          <button key={p.id} type="button" aria-label={`Catch the ${season.particle}`} data-testid={`catch-piece-${i}`} onPointerDown={(e) => caught(i, e)}
            className={`catch-piece pointer-events-auto absolute ${mote ? "catch-rise" : "catch-fall"} -m-5 p-5`}
            style={{ left: `${p.left}%`, animationDuration: `${p.dur}s`, animationDelay: `${p.delay}s`, ["--sway" as any]: `${p.sway}px` }}>
            <span className={`particle-${season.particle} block`} style={{ ["--s" as any]: p.size }} />
          </button>
        ))}
      </div>
      {sparks.map((s) => <Sparkle key={s.id} x={s.x} y={s.y} />)}
      {state && (
        <div className="fixed bottom-3 right-3 z-40 flex items-center gap-2 rounded-full bg-white/95 py-1.5 pl-2 pr-1.5 sm:bottom-5 sm:right-5 sm:py-2 sm:pl-3 sm:pr-2 shadow-xl ring-2 ring-amber-200" data-testid="catch-counter">
          <span className="flex h-9 w-9 items-center justify-center rounded-full" style={{ background: state.season.color }}><Icon className="h-5 w-5 text-white" /></span>
          <div className="leading-tight">
            <p className="text-lg font-semibold text-slate-800" data-testid="catch-today">{state.today} caught</p>
            <p className="hidden text-xs text-slate-500 sm:block" data-testid="catch-goal">{state.earned_this_season ? `${state.season.label} sticker earned!` : `${progress}/${state.goal} for a ${state.season.label} sticker`}</p>
          </div>
          <button onClick={toggleSound} data-testid="catch-sound-toggle" aria-label={sound ? "Turn chime off" : "Turn chime on"} className="ml-1 flex h-8 w-8 items-center justify-center rounded-full bg-amber-50 text-amber-700 hover:bg-amber-100">
            {sound ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
          </button>
        </div>
      )}
      <AnimatePresence>
        {won && (
          <motion.div className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-900/40 p-6" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setWon(null)} data-testid="catch-sticker-won">
            <motion.div initial={{ scale: 0.6, rotate: -8 }} animate={{ scale: 1, rotate: 0 }} transition={{ type: "spring", stiffness: 220, damping: 14 }} className="max-w-sm rounded-[2rem] bg-white p-8 text-center shadow-2xl" onClick={(e) => e.stopPropagation()}>
              <SeasonSticker s={won} big />
              <p className="mt-5 text-3xl font-semibold text-slate-800">You did it!</p>
              <p className="mt-2 text-lg text-slate-600">You caught {state?.goal} and earned the <b>{won.label}</b> sticker. It's in your Sticker Book!</p>
              <button onClick={() => setWon(null)} data-testid="catch-sticker-close" className="mt-6 rounded-full bg-[#81B29A] px-8 py-3 text-lg font-semibold text-white hover:bg-[#6c9c85]">Yay!</button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

export function SeasonSticker({ s, big }: { s: any; big?: boolean }) {
  const Icon = SEASON_ICONS[s.icon] || Leaf;
  const sz = big ? "h-32 w-32" : "h-24 w-24";
  return (
    <div className="flex flex-col items-center gap-2" data-testid={`season-sticker-${s.key}`}>
      <div className={`relative flex ${sz} items-center justify-center rounded-full border-[6px] border-white shadow-lg ring-2 ring-amber-200`} style={{ background: `radial-gradient(circle at 35% 30%, #fff8, transparent 60%), ${s.color}` }}>
        <Icon className={big ? "h-16 w-16 text-white drop-shadow" : "h-11 w-11 text-white drop-shadow"} />
      </div>
      {!big && <p className="text-center text-sm font-semibold text-slate-700">{s.label}<br /><span className="text-xs font-normal text-slate-500">{s.year}</span></p>}
    </div>
  );
}

export function SeasonStickers({ kidId }: { kidId: string }) {
  const [st, setSt] = useState<any>(null);
  useEffect(() => { api.get(`/kids/${kidId}/catches`).then((r) => setSt(r.data)).catch(() => {}); }, [kidId]);
  const list = useMemo(() => st?.stickers || [], [st]);
  if (!st) return null;
  return (
    <section className="mt-10 rounded-[2rem] bg-gradient-to-br from-sky-50 to-amber-50 p-6 sm:p-8" data-testid="kids-season-stickers">
      <h2 className="text-2xl font-semibold text-slate-800">Seasonal stickers</h2>
      <p className="mb-6 text-slate-500">Catch {st.goal} falling {st.season.plural} in one day to earn this season's sticker.</p>
      {list.length ? <div className="flex flex-wrap gap-6">{list.map((s: any) => <SeasonSticker key={s.key} s={s} />)}</div>
        : <p className="rounded-2xl border-2 border-dashed border-amber-200 p-6 text-center text-slate-500" data-testid="season-stickers-empty">No seasonal stickers yet. Tap the falling pieces to start catching!</p>}
    </section>
  );
}
